import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchOpportunities } from '../api/client';
import DataTable from '../components/DataTable';
import SignalBadge from '../components/SignalBadge';
import type { Opportunity } from '../types';

// Market type filter options
const MARKET_TYPES = [
  { value: '', label: 'All Markets' },
  { value: 'h2h', label: 'Moneyline (H2H)' },
  { value: 'spreads', label: 'Spreads' },
  { value: 'totals', label: 'Totals (O/U)' },
];

// Market type display labels
const MARKET_TYPE_LABELS: Record<string, string> = {
  h2h: 'ML',
  spreads: 'SPR',
  totals: 'O/U',
};

export default function Opportunities() {
  const [minEdge, setMinEdge] = useState(0);
  const [marketFilter, setMarketFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['opportunities', { minEdge, market: marketFilter }],
    queryFn: () => fetchOpportunities({ minEdge, market: marketFilter || undefined }),
  });

  const columns = [
    {
      key: 'event',
      header: 'Event',
      render: (opp: Opportunity) => (
        <Link
          to={`/events/${opp.eventId}`}
          className="block hover:text-accent transition-colors"
        >
          <div className="font-medium">
            {opp.event.homeTeam} vs {opp.event.awayTeam}
          </div>
          <div className="text-xs text-gray-400">
            {opp.event.sport} • {formatDate(opp.event.commenceTime)}
          </div>
        </Link>
      ),
    },
    {
      key: 'edge',
      header: 'Edge',
      render: (opp: Opportunity) => (
        <span
          className={`font-bold ${
            opp.adjustedEdge >= 0.05
              ? 'text-success'
              : opp.adjustedEdge >= 0.03
              ? 'text-warning'
              : 'text-gray-400'
          }`}
        >
          {(opp.adjustedEdge * 100).toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'market',
      header: 'Market',
      render: (opp: Opportunity) => (
        <span className={`text-xs px-2 py-0.5 rounded ${
          opp.marketType === 'h2h' ? 'bg-blue-500/20 text-blue-400' :
          opp.marketType === 'spreads' ? 'bg-purple-500/20 text-purple-400' :
          opp.marketType === 'totals' ? 'bg-green-500/20 text-green-400' :
          'bg-gray-500/20 text-gray-400'
        }`}>
          {MARKET_TYPE_LABELS[opp.marketType] || opp.marketType || 'N/A'}
        </span>
      ),
    },
    {
      key: 'signal',
      header: 'Signal',
      render: (opp: Opportunity) => (
        <div className="flex flex-col gap-1">
          <SignalBadge type={opp.signalType} size="sm" />
          <span className="text-xs text-gray-400">
            {opp.rlmDirection} • {(opp.rlmStrength * 100).toFixed(0)}%
          </span>
        </div>
      ),
    },
    {
      key: 'prices',
      header: 'Implied Probability',
      render: (opp: Opportunity) => (
        <div className="text-sm">
          <div>
            Sportsbook: <span className="text-accent">{(opp.impliedProbOdds * 100).toFixed(1)}%</span>
          </div>
          <div>
            Polymarket: <span className="text-blue-400">{(opp.impliedProbPolymarket * 100).toFixed(1)}%</span>
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (opp: Opportunity) => (
        <span
          className={`badge ${
            opp.recommendedAction === 'buy_pm' ? 'badge-success' : 'badge-danger'
          }`}
        >
          {opp.recommendedAction === 'buy_pm' ? 'BUY' : 'SELL'}
        </span>
      ),
    },
    {
      key: 'detected',
      header: 'Detected',
      render: (opp: Opportunity) => (
        <span className="text-sm text-gray-400">{formatTimeAgo(opp.detectedAt)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Opportunities</h1>
        <span className="text-sm text-gray-400">
          {data?.pagination.total || 0} detected edges (all confidence levels)
        </span>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center flex-wrap">
        <select
          value={marketFilter}
          onChange={(e) => setMarketFilter(e.target.value)}
          className="bg-surface-50 border border-gray-700 rounded-md px-3 py-2 text-sm"
        >
          {MARKET_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-400">Min Edge:</label>
          <input
            type="range"
            min="0"
            max="10"
            step="0.5"
            value={minEdge * 100}
            onChange={(e) => setMinEdge(parseFloat(e.target.value) / 100)}
            className="w-32"
          />
          <span className="text-sm w-12">{(minEdge * 100).toFixed(1)}%</span>
        </div>
      </div>

      {/* Summary cards */}
      {data?.opportunities && data.opportunities.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card">
            <div className="text-sm text-gray-400">Avg Edge</div>
            <div className="text-xl font-bold text-success">
              {(
                data.opportunities.reduce((sum, o) => sum + o.adjustedEdge, 0) /
                data.opportunities.length *
                100
              ).toFixed(2)}
              %
            </div>
          </div>
          <div className="card">
            <div className="text-sm text-gray-400">Buy Signals</div>
            <div className="text-xl font-bold">
              {data.opportunities.filter((o) => o.recommendedAction === 'buy_pm').length}
            </div>
          </div>
          <div className="card">
            <div className="text-sm text-gray-400">Sell Signals</div>
            <div className="text-xl font-bold">
              {data.opportunities.filter((o) => o.recommendedAction === 'sell_pm').length}
            </div>
          </div>
        </div>
      )}

      {/* Opportunities Table */}
      <DataTable
        columns={columns}
        data={data?.opportunities || []}
        keyExtractor={(o) => o.id}
        loading={isLoading}
        emptyMessage="No opportunities meet your criteria"
      />
    </div>
  );
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}
